import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Users,
  Mail,
  Shield,
  ShieldOff,
  Trash2,
  Send,
  SendHorizonal,
  UserCheck,
  UserX,
  RefreshCw,
  Search,
  ToggleLeft,
  ToggleRight,
  AlertCircle,
  AlertTriangle,
  CheckCircle,
  Clock,
  XCircle,
  KeyRound,
  RotateCcw,
  Gauge,
  MessageCircle,
  ThumbsUp,
  ThumbsDown,
  Megaphone,
} from 'lucide-react';
import ScrollableTabs from '../components/common/ScrollableTabs';
import * as adminService from '../services/admin';
import * as subscriptionService from '../services/subscription';
import PlanBadge from '../components/common/PlanBadge';
import ThemeSwitcher from '../components/admin/ThemeSwitcher';
import AnnouncementPanel from '../components/admin/AnnouncementPanel';
import OptionToggle from '../components/common/OptionToggle';
import type { RateLimitConfig, RateLimitStatus } from '../services/admin';
import type { AdminStats, AdminUser, PrelaunchSubscription, UserRole, PlanType } from '../types';

type TabType = 'stats' | 'prelaunch' | 'users' | 'announcement' | 'ratelimit';

export default function Admin() {
  const { t, i18n } = useTranslation('admin');
  const [activeTab, setActiveTab] = useState<TabType>('stats');
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [subscriptions, setSubscriptions] = useState<PrelaunchSubscription[]>([]);
  const [selectedSubscriptions, setSelectedSubscriptions] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<UserRole | ''>('');
  const [rateLimitConfig, setRateLimitConfig] = useState<RateLimitConfig | null>(null);
  const [blockedKeys, setBlockedKeys] = useState<RateLimitStatus[]>([]);
  const [activeKeys, setActiveKeys] = useState<RateLimitStatus[]>([]);

  // Charger les statistiques
  const loadStats = useCallback(async () => {
    try {
      const response = await adminService.getAdminStats();
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  }, []);

  // Charger les utilisateurs
  const loadUsers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminService.getUsers({
        role: roleFilter || undefined,
        search: searchTerm || undefined,
      });
      if (response.success && response.data) {
        setUsers(response.data);
      }
    } catch (err) {
      console.error('Error loading users:', err);
      setError(t('rateLimit.usersLoadError'));
    } finally {
      setLoading(false);
    }
  }, [roleFilter, searchTerm]);

  // Charger les pré-inscriptions
  const loadSubscriptions = useCallback(async () => {
    setLoading(true);
    try {
      // Pour certains filtres, on récupère côté serveur puis on filtre côté client
      const followUpFilters = ['follow_up_pending', 'follow_up_interested', 'follow_up_not_interested'];
      const isFollowUpFilter = followUpFilters.includes(statusFilter);
      const backendStatus =
        statusFilter === 'expired' || isFollowUpFilter
          ? 'invited'
          : statusFilter === 'resubmitted'
            ? 'pending'
            : statusFilter;
      const response = await adminService.getPrelaunchSubscriptions({
        status: backendStatus || undefined,
        search: searchTerm || undefined,
      });
      if (response.success && response.data) {
        let filtered = response.data;
        if (statusFilter === 'expired') {
          filtered = filtered.filter(
            (s) => s.invitationExpiresAt && new Date(s.invitationExpiresAt) < new Date()
          );
        } else if (statusFilter === 'follow_up_pending') {
          filtered = filtered.filter(
            (s) => s.followUpSentAt && !s.followUpResponse
          );
        } else if (statusFilter === 'follow_up_interested') {
          filtered = filtered.filter(
            (s) => s.followUpResponse === 'interested'
          );
        } else if (statusFilter === 'follow_up_not_interested') {
          filtered = filtered.filter(
            (s) => s.followUpResponse === 'not_interested'
          );
        } else if (statusFilter === 'resubmitted') {
          filtered = filtered.filter((s) => s.resubmittedAt !== null);
        }
        setSubscriptions(filtered);
      }
    } catch (err) {
      console.error('Error loading subscriptions:', err);
      setError(t('prelaunch.loadError'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, searchTerm]);

  // Charger la configuration du rate limiting
  const loadRateLimitConfig = useCallback(async () => {
    setLoading(true);
    try {
      const [configRes, blockedRes, activeRes] = await Promise.all([
        adminService.getRateLimitConfig(),
        adminService.getBlockedKeys(),
        adminService.getActiveRateLimitKeys(),
      ]);

      if (configRes.success && configRes.data) {
        setRateLimitConfig(configRes.data);
      }
      if (blockedRes.success && blockedRes.data) {
        setBlockedKeys(blockedRes.data);
      }
      if (activeRes.success && activeRes.data) {
        setActiveKeys(activeRes.data);
      }
    } catch (err) {
      console.error('Error loading rate limit config:', err);
      setError(t('rateLimit.configLoadError'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Charger les données selon l'onglet actif
  useEffect(() => {
    setError(null);
    setSuccess(null);

    if (activeTab === 'stats') {
      loadStats();
    } else if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'prelaunch') {
      loadSubscriptions();
    } else if (activeTab === 'ratelimit') {
      loadRateLimitConfig();
      loadUsers(); // Pour avoir la liste des utilisateurs
    }
  }, [activeTab, loadStats, loadUsers, loadSubscriptions, loadRateLimitConfig]);

  // Toggle paiements
  const togglePayments = async () => {
    if (!stats) return;

    setActionLoading('payments');
    try {
      const newValue = !stats.paymentsEnabled;
      const response = await adminService.updateConfig(
        'payments_enabled',
        String(newValue)
      );

      if (response.success) {
        setStats({ ...stats, paymentsEnabled: newValue });
        setSuccess(
          newValue
            ? t('config.paymentsActivated')
            : t('config.paymentsDeactivated')
        );
      }
    } catch {
      setError(t('users.roleUpdateError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Toggle inscription
  const toggleRegistration = async () => {
    if (!stats) return;

    setActionLoading('registration');
    try {
      const newValue = !stats.registrationEnabled;
      const response = await adminService.updateConfig(
        'registration_enabled',
        String(newValue)
      );

      if (response.success) {
        setStats({ ...stats, registrationEnabled: newValue });
        setSuccess(
          newValue
            ? t('stats.registrationOpened')
            : t('stats.registrationClosed')
        );
      }
    } catch (err) {
      setError(t('users.roleUpdateError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Modifier le rôle d'un utilisateur
  const handleUpdateRole = async (userId: string, role: UserRole) => {
    setActionLoading(userId);
    try {
      const response = await adminService.updateUserRole(userId, role);
      if (response.success) {
        setUsers(users.map((u) => (u.id === userId ? { ...u, role } : u)));
        setSuccess(t('users.roleUpdated'));
        loadStats();
      }
    } catch (err) {
      setError(t('users.roleUpdateError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Bloquer/débloquer un utilisateur
  const handleBlockUser = async (userId: string, blocked: boolean) => {
    setActionLoading(userId);
    try {
      const response = await adminService.blockUser(userId, blocked);
      if (response.success) {
        setUsers(users.map((u) => (u.id === userId ? { ...u, isBlocked: blocked } : u)));
        setSuccess(blocked ? t('users.userBlocked') : t('users.userUnblocked'));
        loadStats();
      }
    } catch (err) {
      setError(t('users.blockError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Forcer le changement de mot de passe
  const handleForcePasswordChange = async (userId: string, force: boolean) => {
    if (force && !confirm(t('users.forcePasswordConfirm'))) return;

    setActionLoading(userId);
    try {
      const response = await adminService.forcePasswordChange(userId, force);
      if (response.success) {
        setSuccess(force ? t('users.forcePasswordSuccess') : t('users.forcePasswordRemoved'));
        loadUsers();
      }
    } catch (err) {
      setError(t('users.forcePasswordError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Accorder / retirer un plan
  const handleGrantPlan = async (userId: string, plan: PlanType | null) => {
    setActionLoading(userId);
    try {
      const response = await subscriptionService.setGrantedPlan(userId, plan);
      if (response.success) {
        setUsers(users.map((u) => u.id === userId ? { ...u, grantedPlan: plan } : u));
        setSuccess(plan ? t('users.grantPlanSuccess') : t('users.removePlanSuccess'));
      }
    } catch {
      setError(plan ? t('users.grantPlanError') : t('users.removePlanError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Supprimer un utilisateur
  const handleDeleteUser = async (userId: string) => {
    if (!confirm(t('users.deleteConfirm'))) return;

    setActionLoading(userId);
    try {
      const response = await adminService.deleteUser(userId);
      if (response.success) {
        setUsers(users.filter((u) => u.id !== userId));
        setSuccess(t('users.userDeleted'));
        loadStats();
      }
    } catch (err) {
      setError(t('users.deleteError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Reset le rate limit d'un utilisateur
  const handleResetRateLimit = async (userId: string) => {
    setActionLoading(`ratelimit-${userId}`);
    try {
      const response = await adminService.resetUserRateLimit(userId);
      if (response.success) {
        setSuccess(t('rateLimit.resetSuccess'));
      }
    } catch (err) {
      setError(t('rateLimit.resetError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Sélectionner/désélectionner une pré-inscription
  const toggleSubscriptionSelection = (id: string) => {
    const newSelection = new Set(selectedSubscriptions);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedSubscriptions(newSelection);
  };

  // Vérifier si une invitation est expirée
  const isExpired = (sub: PrelaunchSubscription) => {
    return sub.status === 'invited' && sub.invitationExpiresAt && new Date(sub.invitationExpiresAt) < new Date();
  };

  // Sélectionner tous les pending
  const selectAllPending = () => {
    const pendingIds = subscriptions
      .filter((s) => s.status === 'pending')
      .map((s) => s.id);
    setSelectedSubscriptions(new Set(pendingIds));
  };

  // Sélectionner tous les expirés
  const selectAllExpired = () => {
    const expiredIds = subscriptions
      .filter((s) => isExpired(s))
      .map((s) => s.id);
    setSelectedSubscriptions(new Set(expiredIds));
  };

  // Désélectionner tout
  const deselectAll = () => {
    setSelectedSubscriptions(new Set());
  };

  // Envoyer les invitations sélectionnées
  const handleSendSelected = async () => {
    if (selectedSubscriptions.size === 0) return;

    setActionLoading('send');
    try {
      const response = await adminService.sendInvitations(
        Array.from(selectedSubscriptions)
      );
      if (response.success && response.data) {
        setSuccess(
          t('prelaunch.invitationsSent', { success: response.data.success.length, failed: response.data.failed.length })
        );
        setSelectedSubscriptions(new Set());
        loadSubscriptions();
        loadStats();
      }
    } catch (err) {
      setError(t('prelaunch.sendError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Envoyer à tous les pending
  const handleSendAllPending = async () => {
    if (!confirm(t('prelaunch.sendConfirm'))) return;

    setActionLoading('sendAll');
    try {
      const response = await adminService.sendAllPendingInvitations();
      if (response.success && response.data) {
        setSuccess(
          t('prelaunch.invitationsSent', { success: response.data.success.length, failed: response.data.failed.length })
        );
        loadSubscriptions();
        loadStats();
      }
    } catch (err) {
      setError(t('prelaunch.sendError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Envoyer (ou renvoyer) une invitation individuelle
  const handleSendInvitation = async (id: string, isResend = false) => {
    const successKey = isResend ? 'prelaunch.resendSuccess' : 'prelaunch.sendOneSuccess';
    const errorKey = isResend ? 'prelaunch.resendError' : 'prelaunch.sendOneError';

    setActionLoading(`invite-${id}`);
    try {
      const response = await adminService.sendInvitations([id]);
      if (response.success && response.data) {
        if (response.data.success.length > 0) {
          setSuccess(t(successKey));
          setSelectedSubscriptions((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
          });
          loadSubscriptions();
          loadStats();
        } else {
          setError(t(errorKey));
        }
      }
    } catch (err) {
      setError(t(errorKey));
    } finally {
      setActionLoading(null);
    }
  };

  // Rejeter une pré-inscription
  const handleRejectSubscription = async (id: string) => {
    setActionLoading(id);
    try {
      const response = await adminService.rejectSubscription(id);
      if (response.success) {
        setSubscriptions(
          subscriptions.map((s) => (s.id === id ? { ...s, status: 'rejected' as const } : s))
        );
        setSuccess(t('prelaunch.rejectedSuccess'));
        loadStats();
      }
    } catch (err) {
      setError(t('prelaunch.rejectError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Supprimer une pré-inscription
  const handleDeleteSubscription = async (id: string) => {
    if (!confirm(t('prelaunch.deleteConfirm'))) return;

    setActionLoading(id);
    try {
      const response = await adminService.deleteSubscription(id);
      if (response.success) {
        setSubscriptions(subscriptions.filter((s) => s.id !== id));
        setSuccess(t('prelaunch.deleteSuccess'));
        loadStats();
      }
    } catch (err) {
      setError(t('prelaunch.deleteError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Envoyer un email de relance
  const handleSendFollowUp = async (id: string) => {
    setActionLoading(`followup-${id}`);
    try {
      const response = await adminService.sendFollowUp(id);
      if (response.success) {
        setSuccess(t('prelaunch.followUpSent'));
        loadSubscriptions();
      }
    } catch (err) {
      setError(t('prelaunch.followUpError'));
    } finally {
      setActionLoading(null);
    }
  };

  // Trouver un utilisateur par son ID (pour afficher le nom au lieu de l'UUID)
  const getUserDisplayName = (userId: string): string => {
    const user = users.find((u) => u.id === userId);
    if (!user) return userId.substring(0, 8) + '...';
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return user.email;
  };

  // Formater la date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString(i18n.language, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Obtenir l'icône de statut (prend en compte l'expiration)
  const getStatusIcon = (sub: PrelaunchSubscription) => {
    if (isExpired(sub)) {
      return <AlertTriangle className="w-4 h-4 text-orange-500" />;
    }
    switch (sub.status) {
      case 'pending':
        return <Clock className="w-4 h-4 text-yellow-500" />;
      case 'invited':
        return <Send className="w-4 h-4 text-blue-500" />;
      case 'registered':
        return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 'rejected':
        return <XCircle className="w-4 h-4 text-red-500" />;
      default:
        return null;
    }
  };

  // Obtenir le label de statut (prend en compte l'expiration)
  const getStatusLabel = (sub: PrelaunchSubscription) => {
    if (isExpired(sub)) {
      return t('prelaunch.expired');
    }
    switch (sub.status) {
      case 'pending':
        return t('prelaunch.pending');
      case 'invited':
        return t('prelaunch.invited');
      case 'registered':
        return t('prelaunch.registered');
      case 'rejected':
        return t('prelaunch.rejected');
      default:
        return sub.status;
    }
  };

  // Badge des demandes revenues en file d'attente : un rejet ou une invitation
  // expirée ne disparaît pas en silence, la décision est reproposée à l'admin
  const getResubmissionBadge = (sub: PrelaunchSubscription) => {
    if (!sub.resubmittedAt) return null;

    const origin =
      sub.previousStatus === 'rejected'
        ? t('prelaunch.resubmittedAfterReject', { date: formatDate(sub.rejectedAt || sub.resubmittedAt) })
        : sub.previousStatus === 'registered'
          ? t('prelaunch.resubmittedAfterDeletion')
          : t('prelaunch.resubmittedAfterExpiry');

    return (
      <span
        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700"
        title={origin}
      >
        <RotateCcw className="w-3 h-3" />
        {t('prelaunch.resubmitted', { count: sub.requestCount })}
      </span>
    );
  };

  // Obtenir le badge de réponse au follow-up
  const getFollowUpBadge = (sub: PrelaunchSubscription) => {
    if (!sub.followUpSentAt) return null;
    if (sub.followUpResponse === 'interested') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
          <ThumbsUp className="w-3 h-3" />
          {t('prelaunch.interested')}
        </span>
      );
    }
    if (sub.followUpResponse === 'not_interested') {
      return (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
          <ThumbsDown className="w-3 h-3" />
          {t('prelaunch.notInterested')}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
        <MessageCircle className="w-3 h-3" />
        {t('prelaunch.followUpPending')}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
          <p className="text-gray-600">{t('subtitle')}</p>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-500 hover:text-red-700">
            &times;
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <CheckCircle className="w-5 h-5" />
          {success}
          <button onClick={() => setSuccess(null)} className="ml-auto text-green-500 hover:text-green-700">
            &times;
          </button>
        </div>
      )}

      {/* Tabs */}
      <ScrollableTabs className="-mb-px space-x-4 md:space-x-8">
          <button
            onClick={() => setActiveTab('stats')}
            className={`py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap flex-shrink-0 ${
              activeTab === 'stats'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            {t('stats.title')}
          </button>
          <button
            onClick={() => setActiveTab('prelaunch')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'prelaunch'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Mail className="w-4 h-4" />
            {t('prelaunch.title')}
            {stats && stats.pendingSubscriptions > 0 && (
              <span className="bg-yellow-100 text-yellow-800 text-xs px-2 py-0.5 rounded-full">
                {stats.pendingSubscriptions}
              </span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'users'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Users className="w-4 h-4" />
            {t('users.title')}
          </button>
          <button
            onClick={() => setActiveTab('announcement')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'announcement'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Megaphone className="w-4 h-4" />
            {t('announcement.title')}
          </button>
          <button
            onClick={() => setActiveTab('ratelimit')}
            className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center gap-2 whitespace-nowrap flex-shrink-0 ${
              activeTab === 'ratelimit'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Gauge className="w-4 h-4" />
            {t('rateLimit.title')}
          </button>
      </ScrollableTabs>

      {/* Contenu des onglets */}
      {activeTab === 'stats' && stats && (
        <div className="space-y-6">
          {/* Toggle inscriptions */}
          <div className="bg-white rounded-lg border p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{t('stats.registrationPublic')}</h3>
                <p className="text-sm text-gray-500">
                  {stats.registrationEnabled
                    ? t('stats.registrationOpenDesc')
                    : t('stats.registrationClosedDesc')}
                </p>
              </div>
              <button
                onClick={toggleRegistration}
                disabled={actionLoading === 'registration'}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  stats.registrationEnabled
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {actionLoading === 'registration' ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : stats.registrationEnabled ? (
                  <ToggleRight className="w-5 h-5" />
                ) : (
                  <ToggleLeft className="w-5 h-5" />
                )}
                {stats.registrationEnabled ? t('stats.enabled') : t('stats.disabled')}
              </button>
            </div>
          </div>

          {/* Toggle paiements */}
          <div className="bg-white rounded-lg border p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{t('config.paymentsEnabled')}</h3>
                <p className="text-sm text-gray-500">
                  {stats.paymentsEnabled
                    ? t('config.paymentsEnabledDesc')
                    : t('config.paymentsDisabledDesc')}
                </p>
              </div>
              <button
                onClick={togglePayments}
                disabled={actionLoading === 'payments'}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                  stats.paymentsEnabled
                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {actionLoading === 'payments' ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : stats.paymentsEnabled ? (
                  <ToggleRight className="w-5 h-5" />
                ) : (
                  <ToggleLeft className="w-5 h-5" />
                )}
                {stats.paymentsEnabled ? t('stats.enabled') : t('stats.disabled')}
              </button>
            </div>
          </div>

          {/* Theme switcher */}
          <ThemeSwitcher />

          {/* Statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 rounded-lg">
                  <Users className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('stats.totalUsers')}</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalUsers}</p>
                  <p className="text-xs text-gray-400">
                    {stats.activeUsers} {t('stats.activeUsers').toLowerCase()}, {stats.blockedUsers} {t('stats.blockedUsers')}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-yellow-100 rounded-lg">
                  <Clock className="w-6 h-6 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('stats.pending')}</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.pendingSubscriptions}</p>
                  <p className="text-xs text-gray-400">{t('stats.preRegistrations')}</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-green-100 rounded-lg">
                  <UserCheck className="w-6 h-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t('stats.converted')}</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.registeredSubscriptions}</p>
                  <p className="text-xs text-gray-400">
                    {t('stats.onInvited', { count: stats.invitedSubscriptions + stats.registeredSubscriptions })}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Sources d'inscription */}
          {stats.referralStats && stats.referralStats.length > 0 && (
            <div className="bg-white rounded-lg border p-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('stats.referralSources')}</h3>
              <div className="space-y-3">
                {(() => {
                  const totalReferrals = stats.referralStats.reduce((sum, r) => sum + r.count, 0);
                  const sourceLabels: Record<string, string> = {
                    google: t('referralSources.google'),
                    discord: t('referralSources.discord'),
                    word_of_mouth: t('referralSources.word_of_mouth'),
                    social_media: t('referralSources.social_media'),
                    youtube: t('referralSources.youtube'),
                    friend: t('referralSources.friend'),
                    other: t('referralSources.other'),
                  };
                  return stats.referralStats.map((stat) => {
                    const percent = totalReferrals > 0 ? Math.round((stat.count / totalReferrals) * 100) : 0;
                    return (
                      <div key={stat.source}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-700">{sourceLabels[stat.source] || stat.source}</span>
                          <span className="text-gray-500">{stat.count} ({percent}%)</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div
                            className="bg-primary-600 h-2 rounded-full transition-all"
                            style={{ width: `${percent}%` }}
                          />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
              <p className="mt-3 text-xs text-gray-400">
                {stats.referralStats.reduce((sum, r) => sum + r.count, 0)} réponses sur {stats.totalUsers} inscrits
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'prelaunch' && (
        <div className="space-y-4">
          {/* Barre d'actions */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('prelaunch.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">{t('prelaunch.allStatuses')}</option>
              <option value="pending">{t('prelaunch.pending')}</option>
              <option value="resubmitted">{t('prelaunch.resubmittedFilter')}</option>
              <option value="invited">{t('prelaunch.invitedFilter')}</option>
              <option value="expired">{t('prelaunch.expiredFilter')}</option>
              <option value="registered">{t('prelaunch.registeredFilter')}</option>
              <option value="rejected">{t('prelaunch.rejectedFilter')}</option>
              <option value="follow_up_pending">{t('prelaunch.followUpPendingFilter')}</option>
              <option value="follow_up_interested">{t('prelaunch.interestedFilter')}</option>
              <option value="follow_up_not_interested">{t('prelaunch.notInterestedFilter')}</option>
            </select>

            <button
              onClick={() => loadSubscriptions()}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              title={t('rateLimit.refresh')}
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Actions en masse */}
          <div className="flex flex-wrap items-center gap-2 bg-gray-50 p-4 rounded-lg">
            <button
              onClick={selectAllPending}
              className="px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50"
            >
              {t('prelaunch.selectPending')}
            </button>
            <button
              onClick={selectAllExpired}
              className="px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50"
            >
              {t('prelaunch.selectExpired')}
            </button>
            <button
              onClick={deselectAll}
              className="px-3 py-1.5 text-sm bg-white border rounded-lg hover:bg-gray-50"
            >
              {t('prelaunch.deselectAll')}
            </button>
            <div className="flex-1" />
            <span className="text-sm text-gray-500">
              {t('prelaunch.selectedCount', { count: selectedSubscriptions.size })}
            </span>
            <button
              onClick={handleSendSelected}
              disabled={selectedSubscriptions.size === 0 || actionLoading === 'send'}
              className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {actionLoading === 'send' ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {t('prelaunch.sendSelection')}
            </button>
            <button
              onClick={handleSendAllPending}
              disabled={actionLoading === 'sendAll'}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {actionLoading === 'sendAll' ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <SendHorizonal className="w-4 h-4" />
              )}
              {t('prelaunch.sendAll')}
            </button>
          </div>

          {/* Liste des pré-inscriptions - Vue table (desktop) */}
          <div className="hidden md:block bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    <OptionToggle
                      variant="box"
                      selected={
                        subscriptions.filter((s) => s.status === 'pending').length > 0 &&
                        subscriptions
                          .filter((s) => s.status === 'pending')
                          .every((s) => selectedSubscriptions.has(s.id))
                      }
                      onToggle={() => {
                        const allSelected =
                          subscriptions.filter((s) => s.status === 'pending').length > 0 &&
                          subscriptions
                            .filter((s) => s.status === 'pending')
                            .every((s) => selectedSubscriptions.has(s.id));
                        if (!allSelected) selectAllPending();
                        else deselectAll();
                      }}
                    >
                      {t('prelaunch.selectPending')}
                    </OptionToggle>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Email
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Statut
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date inscription
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Invitation envoyée
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('prelaunch.followUp')}
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      {t('prelaunch.loading')}
                    </td>
                  </tr>
                ) : subscriptions.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      {t('prelaunch.noSubscriptions')}
                    </td>
                  </tr>
                ) : (
                  subscriptions.map((sub) => (
                    <tr key={sub.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <OptionToggle
                          variant="box"
                          selected={selectedSubscriptions.has(sub.id)}
                          onToggle={() => toggleSubscriptionSelection(sub.id)}
                          disabled={sub.status !== 'pending' && sub.status !== 'invited'}
                        >
                          {sub.email}
                        </OptionToggle>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{sub.email}</td>
                      <td className="px-4 py-3">
                        <span className="flex items-center gap-1.5 text-sm">
                          {getStatusIcon(sub)}
                          {getStatusLabel(sub)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(sub.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div>{formatDate(sub.invitationSentAt)}</div>
                        {sub.invitationExpiresAt && (
                          <div className={`text-xs ${isExpired(sub) ? 'text-orange-500 font-medium' : 'text-green-600'}`}>
                            {isExpired(sub) ? t('prelaunch.expiredOn') : t('prelaunch.expiresOn')} {formatDate(sub.invitationExpiresAt)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {getResubmissionBadge(sub)}
                          {getFollowUpBadge(sub)}
                        </div>
                        {sub.followUpSentAt && (
                          <div className="text-xs text-gray-400 mt-1">
                            {formatDate(sub.followUpSentAt)}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          {/* Bouton relance : visible pour les invités expirés sans réponse follow-up */}
                          {isExpired(sub) && !sub.followUpResponse && (
                            <button
                              onClick={() => handleSendFollowUp(sub.id)}
                              disabled={actionLoading === `followup-${sub.id}`}
                              className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"
                              title={t('prelaunch.sendFollowUp')}
                            >
                              {actionLoading === `followup-${sub.id}` ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <MessageCircle className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {sub.status === 'pending' && (
                            <button
                              onClick={() => handleSendInvitation(sub.id)}
                              disabled={actionLoading === `invite-${sub.id}`}
                              className="p-1.5 text-primary-600 hover:bg-primary-50 rounded disabled:opacity-50"
                              title={t('prelaunch.sendInvitation')}
                            >
                              {actionLoading === `invite-${sub.id}` ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <Send className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {sub.status === 'invited' && (
                            <button
                              onClick={() => handleSendInvitation(sub.id, true)}
                              disabled={actionLoading === `invite-${sub.id}`}
                              className="p-1.5 text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
                              title={t('prelaunch.resend')}
                            >
                              {actionLoading === `invite-${sub.id}` ? (
                                <RefreshCw className="w-4 h-4 animate-spin" />
                              ) : (
                                <RotateCcw className="w-4 h-4" />
                              )}
                            </button>
                          )}
                          {sub.status === 'pending' && (
                            <button
                              onClick={() => handleRejectSubscription(sub.id)}
                              disabled={actionLoading === sub.id}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                              title={t('prelaunch.reject')}
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteSubscription(sub.id)}
                            disabled={actionLoading === sub.id}
                            className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                            title={t('delete', { ns: 'common' })}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Liste des pré-inscriptions - Vue carte (mobile) */}
          <div className="md:hidden space-y-3">
            {loading ? (
              <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                {t('prelaunch.loading')}
              </div>
            ) : subscriptions.length === 0 ? (
              <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
                {t('prelaunch.noSubscriptions')}
              </div>
            ) : (
              subscriptions.map((sub) => (
                <div key={sub.id} className="bg-white rounded-lg border p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {(sub.status === 'pending' || sub.status === 'invited') && (
                        <OptionToggle
                          variant="box"
                          selected={selectedSubscriptions.has(sub.id)}
                          onToggle={() => toggleSubscriptionSelection(sub.id)}
                        >
                          {sub.email}
                        </OptionToggle>
                      )}
                      <span className="text-sm font-medium text-gray-900 truncate">{sub.email}</span>
                    </div>
                    <span className="flex items-center gap-1.5 text-sm flex-shrink-0">
                      {getStatusIcon(sub)}
                      {getStatusLabel(sub)}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                    <span>{t('prelaunch.registeredLabel')} {formatDate(sub.createdAt)}</span>
                    {sub.invitationSentAt && <span>{t('prelaunch.invitedLabel')} {formatDate(sub.invitationSentAt)}</span>}
                    {sub.invitationExpiresAt && (
                      <span className={isExpired(sub) ? 'text-orange-500 font-medium' : 'text-green-600'}>
                        {isExpired(sub) ? t('prelaunch.expiredOn') : t('prelaunch.expiresOn')} {formatDate(sub.invitationExpiresAt)}
                      </span>
                    )}
                  </div>
                  {/* Badges d'historique et de relance (mobile) */}
                  {(getResubmissionBadge(sub) || getFollowUpBadge(sub)) && (
                    <div className="flex flex-wrap items-center gap-2">
                      {getResubmissionBadge(sub)}
                      {getFollowUpBadge(sub)}
                    </div>
                  )}
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    {isExpired(sub) && !sub.followUpResponse && (
                      <button
                        onClick={() => handleSendFollowUp(sub.id)}
                        disabled={actionLoading === `followup-${sub.id}`}
                        className="p-1.5 text-amber-600 hover:bg-amber-50 rounded"
                        title={t('prelaunch.sendFollowUp')}
                      >
                        {actionLoading === `followup-${sub.id}` ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <MessageCircle className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    {sub.status === 'pending' && (
                      <button
                        onClick={() => handleSendInvitation(sub.id)}
                        disabled={actionLoading === `invite-${sub.id}`}
                        className="p-1.5 text-primary-600 hover:bg-primary-50 rounded disabled:opacity-50"
                        title={t('prelaunch.sendInvitation')}
                      >
                        {actionLoading === `invite-${sub.id}` ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    {sub.status === 'invited' && (
                      <button
                        onClick={() => handleSendInvitation(sub.id, true)}
                        disabled={actionLoading === `invite-${sub.id}`}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 rounded disabled:opacity-50"
                        title={t('prelaunch.resend')}
                      >
                        {actionLoading === `invite-${sub.id}` ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <RotateCcw className="w-4 h-4" />
                        )}
                      </button>
                    )}
                    {sub.status === 'pending' && (
                      <button
                        onClick={() => handleRejectSubscription(sub.id)}
                        disabled={actionLoading === sub.id}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                        title={t('prelaunch.reject')}
                      >
                        <XCircle className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleDeleteSubscription(sub.id)}
                      disabled={actionLoading === sub.id}
                      className="p-1.5 text-gray-600 hover:bg-gray-100 rounded"
                      title={t('delete', { ns: 'common' })}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="space-y-4">
          {/* Barre d'actions */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder={t('users.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
            </div>

            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as UserRole | '')}
              className="px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="">{t('users.allRoles')}</option>
              <option value="user">{t('users.users')}</option>
              <option value="admin">{t('users.admins')}</option>
            </select>

            <button
              onClick={() => loadUsers()}
              className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
              title={t('rateLimit.refresh')}
            >
              <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Liste des utilisateurs - Vue table (desktop) */}
          <div className="hidden md:block bg-white rounded-lg border overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Utilisateur
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Rôle
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Statut
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    {t('users.plan')}
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Inscription
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Source
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Dernière connexion
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-8 text-center text-gray-500">
                      <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                      {t('prelaunch.loading')}
                    </td>
                  </tr>
                ) : users.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-4 py-8 text-center text-gray-500">
                      {t('users.noUsers')}
                    </td>
                  </tr>
                ) : (
                  users.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {user.firstName || user.lastName
                              ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                              : '-'}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={user.role}
                          onChange={(e) =>
                            handleUpdateRole(user.id, e.target.value as UserRole)
                          }
                          disabled={actionLoading === user.id}
                          className={`text-sm border rounded px-2 py-1 ${
                            user.role === 'admin'
                              ? 'bg-purple-50 text-purple-700 border-purple-200'
                              : 'bg-gray-50 text-gray-700 border-gray-200'
                          }`}
                        >
                          <option value="user">{t('users.user')}</option>
                          <option value="admin">{t('users.admin')}</option>
                        </select>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-col gap-1">
                          {user.isBlocked ? (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                              <ShieldOff className="w-3 h-3" />
                              Bloqué
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                              <Shield className="w-3 h-3" />
                              Actif
                            </span>
                          )}
                          {!user.emailVerified && (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                              {t('users.emailNotVerified')}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <PlanBadge plan={user.grantedPlan || user.effectivePlan || 'free'} granted={!!user.grantedPlan} />
                          <select
                            value=""
                            onChange={(e) => {
                              const val = e.target.value;
                              if (val === 'remove') handleGrantPlan(user.id, null);
                              else if (val) handleGrantPlan(user.id, val as PlanType);
                            }}
                            disabled={actionLoading === user.id}
                            className="text-xs border border-gray-200 rounded px-1 py-0.5 bg-white text-gray-500"
                          >
                            <option value="">{t('users.grantPlan')}</option>
                            <option value="premium">Premium</option>
                            <option value="duo">Duo</option>
                            {user.grantedPlan && <option value="remove">{t('users.removePlan')}</option>}
                          </select>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(user.createdAt)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {(() => {
                          const sourceLabels: Record<string, string> = {
                            google: t('referralSourcesShort.google'),
                            discord: t('referralSourcesShort.discord'),
                            word_of_mouth: t('referralSourcesShort.word_of_mouth'),
                            social_media: t('referralSourcesShort.social_media'),
                            youtube: t('referralSourcesShort.youtube'),
                            friend: t('referralSourcesShort.friend'),
                            other: t('referralSourcesShort.other'),
                          };
                          return user.referralSource ? sourceLabels[user.referralSource] || user.referralSource : '-';
                        })()}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {formatDate(user.lastLogin)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleBlockUser(user.id, !user.isBlocked)}
                            disabled={actionLoading === user.id}
                            className={`p-1.5 rounded ${
                              user.isBlocked
                                ? 'text-green-600 hover:bg-green-50'
                                : 'text-orange-600 hover:bg-orange-50'
                            }`}
                            title={user.isBlocked ? t('users.unblock') : t('users.block')}
                          >
                            {user.isBlocked ? (
                              <UserCheck className="w-4 h-4" />
                            ) : (
                              <UserX className="w-4 h-4" />
                            )}
                          </button>
                          <button
                            onClick={() => handleForcePasswordChange(user.id, true)}
                            disabled={actionLoading === user.id}
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                            title={t('users.forcePasswordChange')}
                          >
                            <KeyRound className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleResetRateLimit(user.id)}
                            disabled={actionLoading === `ratelimit-${user.id}`}
                            className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
                            title={t('users.resetRateLimit')}
                          >
                            <RotateCcw className={`w-4 h-4 ${actionLoading === `ratelimit-${user.id}` ? 'animate-spin' : ''}`} />
                          </button>
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            disabled={actionLoading === user.id}
                            className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                            title={t('delete', { ns: 'common' })}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Liste des utilisateurs - Vue carte (mobile) */}
          <div className="md:hidden space-y-3">
            {loading ? (
              <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
                <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
                {t('prelaunch.loading')}
              </div>
            ) : users.length === 0 ? (
              <div className="bg-white rounded-lg border p-8 text-center text-gray-500">
                {t('users.noUsers')}
              </div>
            ) : (
              users.map((user) => (
                <div key={user.id} className="bg-white rounded-lg border p-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900">
                        {user.firstName || user.lastName
                          ? `${user.firstName || ''} ${user.lastName || ''}`.trim()
                          : '-'}
                      </div>
                      <div className="text-sm text-gray-500 truncate">{user.email}</div>
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      {user.isBlocked ? (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                          <ShieldOff className="w-3 h-3" />
                          Bloqué
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                          <Shield className="w-3 h-3" />
                          Actif
                        </span>
                      )}
                      {!user.emailVerified && (
                        <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700">
                          {t('users.emailNotVerified')}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                    <span>
                      <select
                        value={user.role}
                        onChange={(e) => handleUpdateRole(user.id, e.target.value as UserRole)}
                        disabled={actionLoading === user.id}
                        className={`text-xs border rounded px-2 py-0.5 ${
                          user.role === 'admin'
                            ? 'bg-purple-50 text-purple-700 border-purple-200'
                            : 'bg-gray-50 text-gray-700 border-gray-200'
                        }`}
                      >
                        <option value="user">{t('users.user')}</option>
                        <option value="admin">{t('users.admin')}</option>
                      </select>
                    </span>
                    <span>{t('users.registeredOn')} {formatDate(user.createdAt)}</span>
                    <span>
                      {(() => {
                        const sourceLabels: Record<string, string> = {
                          google: t('referralSourcesShort.google'),
                          discord: t('referralSourcesShort.discord'),
                          word_of_mouth: t('referralSourcesShort.word_of_mouth'),
                          social_media: t('referralSourcesShort.social_media'),
                          youtube: t('referralSourcesShort.youtube'),
                          friend: t('referralSourcesShort.friend'),
                          other: t('referralSourcesShort.other'),
                        };
                        return user.referralSource ? `${t('users.sourceLabel')} ${sourceLabels[user.referralSource] || user.referralSource}` : '';
                      })()}
                    </span>
                    {user.lastLogin && <span>{t('users.lastLoginShort')} {formatDate(user.lastLogin)}</span>}
                  </div>
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-100">
                    <button
                      onClick={() => handleBlockUser(user.id, !user.isBlocked)}
                      disabled={actionLoading === user.id}
                      className={`p-1.5 rounded ${
                        user.isBlocked
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-orange-600 hover:bg-orange-50'
                      }`}
                      title={user.isBlocked ? t('users.unblock') : t('users.block')}
                    >
                      {user.isBlocked ? (
                        <UserCheck className="w-4 h-4" />
                      ) : (
                        <UserX className="w-4 h-4" />
                      )}
                    </button>
                    <button
                      onClick={() => handleForcePasswordChange(user.id, true)}
                      disabled={actionLoading === user.id}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 rounded"
                      title={t('users.forcePasswordChange')}
                    >
                      <KeyRound className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleResetRateLimit(user.id)}
                      disabled={actionLoading === `ratelimit-${user.id}`}
                      className="p-1.5 text-purple-600 hover:bg-purple-50 rounded"
                      title={t('users.resetRateLimit')}
                    >
                      <RotateCcw className={`w-4 h-4 ${actionLoading === `ratelimit-${user.id}` ? 'animate-spin' : ''}`} />
                    </button>
                    <button
                      onClick={() => handleDeleteUser(user.id)}
                      disabled={actionLoading === user.id}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded"
                      title={t('delete', { ns: 'common' })}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {activeTab === 'announcement' && (
        <AnnouncementPanel onError={setError} onSuccess={setSuccess} />
      )}

      {activeTab === 'ratelimit' && (
        <div className="space-y-6">
          {/* Clés bloquées */}
          {blockedKeys.length > 0 && (
            <div className="bg-red-50 rounded-lg border border-red-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <AlertCircle className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-medium text-red-800">
                  {t('rateLimit.activeBlocks')} ({blockedKeys.length})
                </h3>
              </div>
              {/* Vue table (desktop) */}
              <div className="hidden md:block overflow-hidden rounded-lg border border-red-200">
                <table className="min-w-full divide-y divide-red-200">
                  <thead className="bg-red-100">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-red-700 uppercase">{t('rateLimit.type')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-red-700 uppercase">{t('rateLimit.identifier')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-red-700 uppercase">{t('rateLimit.limiter')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-red-700 uppercase">{t('rateLimit.resetIn')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-red-700 uppercase">{t('rateLimit.action')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-red-100">
                    {blockedKeys.map((item) => (
                      <tr key={item.key} className="hover:bg-red-50">
                        <td className="px-4 py-3 text-sm">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            item.type === 'user' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {item.type === 'user' ? t('rateLimit.userType') : t('rateLimit.ipType')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {item.type === 'user' ? getUserDisplayName(item.identifier) : <span className="font-mono">{item.identifier}</span>}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600 capitalize">{item.limiter}</td>
                        <td className="px-4 py-3 text-sm text-gray-600">
                          {item.ttl > 0 ? `${Math.ceil(item.ttl / 60)} min` : '-'}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={() => {
                              if (item.type === 'user') {
                                handleResetRateLimit(item.identifier);
                              } else {
                                adminService.resetIpRateLimit(item.identifier).then(() => {
                                  setSuccess(t('rateLimit.resetSuccess'));
                                  loadRateLimitConfig();
                                });
                              }
                            }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                          >
                            <RotateCcw className="w-3 h-3" />
                            {t('rateLimit.unblock')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Vue carte (mobile) */}
              <div className="md:hidden space-y-2">
                {blockedKeys.map((item) => (
                  <div key={item.key} className="bg-white rounded-lg border border-red-200 p-3 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                          item.type === 'user' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {item.type === 'user' ? t('rateLimit.userType') : t('rateLimit.ipType')}
                        </span>
                        <span className="text-sm text-gray-900 truncate">
                          {item.type === 'user' ? getUserDisplayName(item.identifier) : item.identifier}
                        </span>
                      </div>
                      <button
                        onClick={() => {
                          if (item.type === 'user') {
                            handleResetRateLimit(item.identifier);
                          } else {
                            adminService.resetIpRateLimit(item.identifier).then(() => {
                              setSuccess(t('rateLimit.resetSuccess'));
                              loadRateLimitConfig();
                            });
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 flex-shrink-0"
                      >
                        <RotateCcw className="w-3 h-3" />
                        {t('rateLimit.unblock')}
                      </button>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500">
                      <span className="capitalize">{item.limiter}</span>
                      <span>{t('rateLimit.resetIn')} : {item.ttl > 0 ? `${Math.ceil(item.ttl / 60)} min` : '-'}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Configuration du Rate Limiting */}
          <div className="bg-white rounded-lg border p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">{t('rateLimit.config')}</h3>
              <button
                onClick={loadRateLimitConfig}
                className="inline-flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </button>
            </div>
            {rateLimitConfig ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(rateLimitConfig).map(([key, config]) => (
                  <div key={key} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Gauge className="w-4 h-4 text-primary-600" />
                      <span className="font-medium text-gray-900 capitalize">{key}</span>
                    </div>
                    <p className="text-sm text-gray-500 mb-2">{config.description}</p>
                    <div className="text-sm">
                      <span className="font-medium text-gray-900">{config.max}</span>
                      <span className="text-gray-500"> {t('rateLimit.requests')} / {config.windowFormatted}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center justify-center py-8">
                <RefreshCw className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            )}
          </div>

          {/* Quotas en temps réel */}
          <div className="bg-white rounded-lg border p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              {t('rateLimit.activeQuotas')} ({activeKeys.length} {t('rateLimit.activeKeys')})
            </h3>
            {activeKeys.length === 0 ? (
              <p className="text-sm text-gray-500">{t('rateLimit.noActivity')}</p>
            ) : (
              <>
              {/* Vue table (desktop) */}
              <div className="hidden md:block overflow-hidden rounded-lg border">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rateLimit.type')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rateLimit.identifier')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rateLimit.limiter')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rateLimit.usage')}</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('rateLimit.resetIn')}</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">{t('rateLimit.action')}</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {activeKeys.slice(0, 20).map((item) => {
                      const percentage = (item.count / item.max) * 100;
                      const isWarning = percentage >= 80;
                      const isBlocked = item.remaining === 0;

                      return (
                        <tr key={item.key} className={`hover:bg-gray-50 ${isBlocked ? 'bg-red-50' : ''}`}>
                          <td className="px-4 py-3 text-sm">
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              item.type === 'user' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                            }`}>
                              {item.type === 'user' ? t('rateLimit.userType') : t('rateLimit.ipType')}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {item.type === 'user' ? getUserDisplayName(item.identifier) : <span className="font-mono text-xs">{item.identifier}</span>}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600 capitalize">{item.limiter}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden max-w-[100px]">
                                <div
                                  className={`h-full rounded-full ${
                                    isBlocked ? 'bg-red-500' : isWarning ? 'bg-yellow-500' : 'bg-green-500'
                                  }`}
                                  style={{ width: `${Math.min(percentage, 100)}%` }}
                                />
                              </div>
                              <span className={`text-xs font-medium ${
                                isBlocked ? 'text-red-600' : isWarning ? 'text-yellow-600' : 'text-gray-600'
                              }`}>
                                {item.count}/{item.max}
                              </span>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">
                            {item.ttl > 0 ? `${Math.ceil(item.ttl / 60)} min` : '-'}
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() => {
                                if (item.type === 'user') {
                                  handleResetRateLimit(item.identifier);
                                } else {
                                  adminService.resetIpRateLimit(item.identifier).then(() => {
                                    setSuccess(t('rateLimit.resetSuccess'));
                                    loadRateLimitConfig();
                                  });
                                }
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                            >
                              <RotateCcw className="w-3 h-3" />
                              {t('rateLimit.reset')}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                {activeKeys.length > 20 && (
                  <div className="px-4 py-2 bg-gray-50 text-sm text-gray-500 text-center">
                    {t('rateLimit.showingFirst', { shown: 20, total: activeKeys.length })}
                  </div>
                )}
              </div>

              {/* Vue carte (mobile) */}
              <div className="md:hidden space-y-2">
                {activeKeys.slice(0, 20).map((item) => {
                  const percentage = (item.count / item.max) * 100;
                  const isWarning = percentage >= 80;
                  const isBlocked = item.remaining === 0;

                  return (
                    <div key={item.key} className={`rounded-lg border p-3 space-y-2 ${isBlocked ? 'bg-red-50 border-red-200' : 'bg-white'}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 ${
                            item.type === 'user' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-700'
                          }`}>
                            {item.type === 'user' ? t('rateLimit.userType') : t('rateLimit.ipType')}
                          </span>
                          <span className="text-sm text-gray-900 truncate">
                            {item.type === 'user' ? getUserDisplayName(item.identifier) : item.identifier}
                          </span>
                        </div>
                        <button
                          onClick={() => {
                            if (item.type === 'user') {
                              handleResetRateLimit(item.identifier);
                            } else {
                              adminService.resetIpRateLimit(item.identifier).then(() => {
                                setSuccess(t('rateLimit.resetSuccess'));
                                loadRateLimitConfig();
                              });
                            }
                          }}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200 flex-shrink-0"
                        >
                          <RotateCcw className="w-3 h-3" />
                          {t('rateLimit.reset')}
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500 capitalize flex-shrink-0">{item.limiter}</span>
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${
                              isBlocked ? 'bg-red-500' : isWarning ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${Math.min(percentage, 100)}%` }}
                          />
                        </div>
                        <span className={`text-xs font-medium flex-shrink-0 ${
                          isBlocked ? 'text-red-600' : isWarning ? 'text-yellow-600' : 'text-gray-600'
                        }`}>
                          {item.count}/{item.max}
                        </span>
                        <span className="text-xs text-gray-500 flex-shrink-0">
                          {item.ttl > 0 ? `${Math.ceil(item.ttl / 60)}m` : '-'}
                        </span>
                      </div>
                    </div>
                  );
                })}
                {activeKeys.length > 20 && (
                  <div className="text-sm text-gray-500 text-center py-2">
                    Affichage des 20 premiers sur {activeKeys.length} clés actives
                  </div>
                )}
              </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
